import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _isObject from 'lodash/isObject';
import _get from 'lodash/get';
import _some from 'lodash/some';
import _flattenDeep from 'lodash/flattenDeep';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { variableRegex } from 'containers/Assessment/constants';
import {
  makeSelectAnswers,
  makeSelectAssessmentResult,
  makeSelectAssessment,
} from 'containers/Assessment/selectors';
import { getAssessmentJSON } from 'containers/Assessment/utils';
import { makeSelectProfile } from 'containers/Auth/selectors';
import AppContainer from 'components/AppContainer';
import ArrowIcon from 'components/BackHeader/images/arrow-back.svg';
import LoadingIndicator from 'components/LoadingIndicator';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';

const useStyles = makeStyles(() => ({
  root: {
    background: '#f8f9f9',
    height: '100%',
    color: '#4B4B4A',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  viewButton: {
    padding: 12,
    borderRadius: 5,
    background: '#FFFFFF',
    color: '#5f5f5f',
    border: `1px solid #e6e6e6`,
    fontFamily: 'MadaRegular',
    fontSize: 'medium',
    display: 'flex',
    justifyContent: 'space-evenly',
    maxWidth: '150px',
    width: '100%',
    '&:hover': {
      opacity: 0.8,
    },
    '& img': {
      marginLeft: '-20px',
      marginRight: '10px',
      color: '#5f5f55',
    },
  },
  gridContainer: {
    background: '#FFFFFF',
    marginTop: 20,
    padding: 44,
  },
  questionsGrid: {
    paddingTop: 25,
  },
  answersGrid: {
    paddingTop: ({ visiblePdfClinicalContent }) =>
      visiblePdfClinicalContent ? 5 : 20,
  },
  subquestions: {
    fontSize: '1rem',
  },
  answers: {
    fontSize: '1rem',
    fontWeight: 200,
    padding: ({ visiblePdfClinicalContent }) =>
      visiblePdfClinicalContent ? '5px 0px' : '10px 0px',
  },
  answersWrapper: {
    marginTop: ({ visiblePdfClinicalContent }) =>
      visiblePdfClinicalContent ? 10 : 16,
  },
}));

const AssessmentClinicalResultDetail = ({
  scoreComponents,
  visiblePdfClinicalContent,
}) => {
  const { code, id } = useParams();
  const classes = useStyles({ visiblePdfClinicalContent });
  const history = useHistory();
  const answers = useSelector(makeSelectAnswers());
  const [siteCopy] = useSiteCopySelector(['assessment-clinical-view']);
  const [siteConfig] = useSiteConfigSelector(['Insurance']);

  const assessmentResult = useSelector(makeSelectAssessmentResult());
  const assessment = useSelector(makeSelectAssessment());
  const profile = useSelector(makeSelectProfile());

  const noResponsesLabel = _get(siteCopy, 'pageCopy.noResponses', '');

  useEffect(() => {
    if (id && _isEmpty(answers)) {
      history.replace(`/assessments/clinicalresults/${code}`);
    }
  }, [answers, code, history, id]);

  useEffect(() => {
    if (!_isEmpty(answers) && !visiblePdfClinicalContent) {
      Mixpanel.track('Assessment Clinical Results Details Viewed', {
        assessment: _get(assessment, 'items.0.fields.name'),
        entryId: _get(assessment, 'items.0.sys.id'),
        type: id,
      });
    }
  }, [answers, assessment, id, visiblePdfClinicalContent]);

  if (_isObject(answers) && _isEmpty(answers)) return null;
  if (code && (_isEmpty(answers) || _isEmpty(assessmentResult)))
    return <LoadingIndicator />;

  const assessmentObject = _get(assessment, 'items.0.fields.assessment');
  const model =
    !_isEmpty(assessment) &&
    getAssessmentJSON(
      assessmentObject,
      null,
      false,
      profile,
      _get(siteConfig, 'config.assessmentQuestionNames'),
    );
  const regularComponent = name =>
    _get(assessmentResult, 'scoringsObject.regularComponents', []).filter(
      cmp => cmp.name === name,
    );
  const userAnswers = _get(answers, 'result', {});
  const allQuestions = _flattenDeep(
    model &&
      model.pages.map(page =>
        page.elements.map(element => {
          if (element.type === 'panel') {
            return element.elements;
          }
          return element;
        }),
      ),
  );
  const relatedQuestionsfn = name => {
    const regularCompnt = regularComponent(name);
    let expression = '';
    regularCompnt.map(comp => {
      expression = expression.concat(` + ${_get(comp, 'expression', '')}`);
      return expression;
    });
    const relatedQuestionsNames = expression.match(variableRegex) || [];
    const relatedQuestions = allQuestions.filter(question => {
      if (question.type === 'matrix') {
        return _some(question.rows, row =>
          relatedQuestionsNames.includes(`[${question.name}[${row.value}]]`),
        );
      }
      return relatedQuestionsNames.includes(`[${question.name}]`);
    });
    return relatedQuestions;
  };
  const content = name => {
    const relatedQuestions = relatedQuestionsfn(name);
    return (
      <Grid item xs={12} className={classes.gridContainer}>
        <Typography variant="h5" color="textPrimary" gutterBottom>
          {name}
        </Typography>
        <Grid container spacing={2} className={classes.answersWrapper}>
          {relatedQuestions.length > 0 ? (
            relatedQuestions.map((item, index) => (
              <Grid item xs={12} key={item.title}>
                <Typography variant="h6" color="textPrimary" gutterBottom>
                  {`${index + 1}. ${item.title}`}
                </Typography>
                <Grid container>
                  {_get(item, 'rows', []).map(ele => (
                    <Grid
                      item
                      xs={12}
                      className={classes.answersGrid}
                      key={ele.value}
                    >
                      <Typography
                        color="textPrimary"
                        variant="h6"
                        className={classes.subquestions}
                        gutterBottom
                      >
                        {`- ${ele.text}`}
                      </Typography>
                      {_isEmpty(_get(userAnswers, `${item.name}`, '')) ? (
                        <Typography
                          color="textPrimary"
                          className={classes.answers}
                        >
                          {_get(siteCopy, 'pageCopy.notAnsweredLabel', '')}
                        </Typography>
                      ) : (
                        _get(item, 'columns', []).map(col => (
                          <Grid key={col.value}>
                            {col.value ===
                              _get(
                                userAnswers,
                                `${item.name}.${ele.value}`,
                                '',
                              ) && (
                              <Typography
                                color="textPrimary"
                                className={classes.answers}
                              >
                                {col.text}
                              </Typography>
                            )}
                          </Grid>
                        ))
                      )}
                    </Grid>
                  ))}
                </Grid>
                <Grid container>
                  {_get(item, 'type', '') !== 'matrix' &&
                  _isEmpty(_get(userAnswers, `${item.name}`, '')) ? (
                    <Typography color="textPrimary" className={classes.answers}>
                      {_get(siteCopy, 'pageCopy.notAnsweredLabel', '')}
                    </Typography>
                  ) : _get(item, 'type', '') === 'boolean' ? (
                    <Grid key={item.name}>
                      <Typography
                        color="textPrimary"
                        className={classes.answers}
                      >
                        {_get(userAnswers, `${item.name}`, '') === '0'
                          ? item.labelFalse
                          : item.labelTrue}
                      </Typography>
                    </Grid>
                  ) : (
                    _get(item, 'choices', []).map(
                      ele =>
                        ele.value === userAnswers[item.name] && (
                          <Grid key={ele.value}>
                            <Typography
                              color="textPrimary"
                              className={classes.answers}
                            >
                              {ele.text}
                            </Typography>
                          </Grid>
                        ),
                    )
                  )}
                </Grid>
              </Grid>
            ))
          ) : (
            <Grid className={classes.questionsGrid}>
              <Typography color="textPrimary">{noResponsesLabel}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      {_isEmpty(scoreComponents) && code ? (
        <div className={classes.root}>
          <AppContainer justify="start">
            <Grid container direction="row">
              <Grid item xs={12} className={classes.buttonContainer}>
                <ButtonBase
                  className={classes.viewButton}
                  onClick={() =>
                    history.push(
                      `/assessments/clinicalresults/${answers.accessCode}`,
                    )
                  }
                >
                  <img alt="back" src={ArrowIcon} />
                  Back
                </ButtonBase>
              </Grid>
              {content(id)}
            </Grid>
          </AppContainer>
        </div>
      ) : (
        <Grid>
          {visiblePdfClinicalContent && (
            <Grid>
              {scoreComponents.length > 0 &&
                scoreComponents.map(comp => <Grid>{content(comp.name)}</Grid>)}
            </Grid>
          )}
        </Grid>
      )}
    </div>
  );
};

export default AssessmentClinicalResultDetail;
